import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MessageNotificationService } from 'message-notification-service-lib';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import {
  CONDITIONAL_FIELDS_ENTITY_NAME,
  IFieldMeta,
  IMAGE_FIELDS_ENTITY_NAME,
  IMAGE_FIELDS_ENTITY_NAME_LABEL_PAIR,
  SCRIPT_FIELDS_NAMESPACE,
} from '../../../interfaces/imetadata';
import { ContentControlToFieldMapService, SerializedFieldContext } from '../../../services/content-control-to-field-map.service';
import { ControllerService } from '../../../services/controller.service';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { LoggingService } from '../../../services/logging.service';
import { ImageFieldOptions, ImageFieldType } from '../../../utils/fieldconfig';
import { FieldContext } from '../../../utils/fieldcontext';
import { FieldType } from '../../../utils/fieldtype';
import { templateContext } from '../../../utils/template-context';
import { IReInkable } from '../ireinkable';
import { IValidStateChangeProvider, ValidStateProvider } from '../valid-state-provider';
import { FileTypeProviderService } from './file-type-provider.service';

enum Mode {
  INSERT,
  UPDATE,
}
enum FieldSource {
  ENTITY,
  SCRIPT,
}
enum ToggleState {
  URL,
  FILE_TYPE,
  FIELD,
}

// I'm not too proud of this UI code. It has too much state and too many if-tests.

@Component({
  selector: 'app-image-fields',
  templateUrl: './image-fields.component.html',
  styleUrls: ['../field-selection.component.scss', '../../../../assets/css-imports/adv2-word-addin/generic-tab-with-form.scss'],
  standalone: false,
})
export class ImageFieldsComponent implements OnInit, IValidStateChangeProvider, IReInkable {
  readonly ImageFieldType = ImageFieldType;
  readonly Mode = Mode;
  readonly ToggleState = ToggleState;

  fileTypes: { id: string; label: string }[] = [];
  toggleState = ToggleState.URL; // the toggle state - URL, FIELD, FILE_TYPE
  imageSource: ImageFieldType = ImageFieldType.URL; // the image source - URL, ENTITY_FIELD, SCRIPT_FIELD, FILE_TYPE
  urlValue: ImageFieldOptions = { type: ImageFieldType.URL, value: '' };
  entityFieldValue: ImageFieldOptions = {
    type: ImageFieldType.ENTITY_FIELD,
    value: '',
  };
  scriptFieldValue: ImageFieldOptions = {
    type: ImageFieldType.SCRIPT_FIELD,
    value: '',
  };
  selectedFileType: { id: string; label: string } | undefined = undefined; // the selected file type for FILE_TYPE image source
  isValid = false;
  mode = Mode.INSERT;
  entityFields: IFieldMeta[] = [];

  private fieldSource: FieldSource = FieldSource.ENTITY; // whether to use ENTITY_FIELD or SCRIPT_FIELD
  private valid = new ValidStateProvider();
  private static SCRIPT_FIELD_PREFIX = `${SCRIPT_FIELDS_NAMESPACE}.out.`;
  private activeFieldContext: FieldContext | undefined;

  @ViewChild(MatTabGroup) tabGroup: MatTabGroup | undefined;

  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private controllerService: ControllerService,
    private fileTypeProviderService: FileTypeProviderService,
    private identityStorage: IdentityStorage,
    private messageNotificationService: MessageNotificationService,
    private loggingService: LoggingService
  ) {}

  ngOnInit() {
    const token = this.identityStorage.get().token;
    const templateTargetEntity = templateContext.getTargetEntity().entity;
    this.fileTypeProviderService
      .getFileTypeProvider(this.getNamespace())
      .getFileTypes(token, templateTargetEntity)
      .pipe(
        tap({
          next: (fileTypes) => (this.fileTypes = fileTypes),
          error: (err) => this.messageNotificationService.error('Failed to get file types.'),
        }),
        finalize(() => {
          this.activeContentControlMonitor.activeContentControlChanged$.subscribe((acc) => this.activeControlChanged(acc));
        })
      )
      .subscribe();

    const isSimpleField = (f: IFieldMeta) =>
      f.type === FieldType.STRING || f.type === FieldType.INTEGER || f.type === FieldType.DOUBLE || f.type === FieldType.DATE;
    this.entityFields = templateContext
      .getTargetEntity()
      .fields.filter((f) => isSimpleField(f))
      .sort((a, b) => a.label?.localeCompare(b.label));
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  reInkMatTab(): void {
    setTimeout(() => {
      this.tabGroup?.realignInkBar();
    }, 0);
  }

  onEntityFieldChosen(): void {
    this.scriptFieldValue.value = '';
    this.fieldSource = FieldSource.ENTITY;
    this.onSourceChanged(ImageFieldType.ENTITY_FIELD);
  }

  scriptDisplayValue(): string {
    return this.scriptFieldValue.value.substring(ImageFieldsComponent.SCRIPT_FIELD_PREFIX.length);
  }

  onScriptFieldChosen(inputText: string): void {
    this.entityFieldValue.value = '';
    this.scriptFieldValue.value = `${ImageFieldsComponent.SCRIPT_FIELD_PREFIX}${inputText}`;
    this.fieldSource = FieldSource.SCRIPT;
    this.onSourceChanged(ImageFieldType.SCRIPT_FIELD);
  }

  disableTab(toggle: ToggleState): boolean {
    if (this.mode === Mode.INSERT) {
      return false;
    }

    if (toggle === ToggleState.URL) {
      return this.imageSource !== ImageFieldType.URL;
    }

    if (toggle === ToggleState.FILE_TYPE) {
      return this.imageSource !== ImageFieldType.FILE_TYPE;
    }

    if (toggle === ToggleState.FIELD) {
      return this.imageSource !== ImageFieldType.ENTITY_FIELD && this.imageSource !== ImageFieldType.SCRIPT_FIELD;
    }

    throw new Error(`Unknown toggle state value '${toggle}'`);
  }

  onTabChanged(event: MatTabChangeEvent): void {
    if (event.index === ToggleState.URL) {
      this.toggleState = ToggleState.URL;
      this.onSourceChanged(ImageFieldType.URL);
    } else if (event.index === ToggleState.FIELD) {
      this.toggleState = ToggleState.FIELD;
      if (this.fieldSource === FieldSource.ENTITY) {
        this.onSourceChanged(ImageFieldType.ENTITY_FIELD);
      } else {
        this.onSourceChanged(ImageFieldType.SCRIPT_FIELD);
      }
    } else if (event.index === ToggleState.FILE_TYPE) {
      this.toggleState = ToggleState.FILE_TYPE;
      this.onSourceChanged(ImageFieldType.FILE_TYPE);
    }
  }

  onSourceChanged(imageSource: ImageFieldType): void {
    this.imageSource = imageSource;
    this.updateValidity();
  }

  onActionClick(): void {
    let value: ImageFieldOptions;
    if (this.imageSource === ImageFieldType.URL) {
      value = this.urlValue;
    } else if (this.imageSource === ImageFieldType.ENTITY_FIELD) {
      value = this.entityFieldValue;
    } else if (this.imageSource === ImageFieldType.SCRIPT_FIELD) {
      value = this.scriptFieldValue;
    } else if (this.imageSource === ImageFieldType.FILE_TYPE) {
      value = {
        type: ImageFieldType.FILE_TYPE,
        value: this.selectedFileType!.id,
      };
    } else {
      throw new Error(`Unknown image source type '${this.imageSource}'.`);
    }

    if (this.mode === Mode.INSERT) {
      this.insertImageField(value.type, value.value);
    } else {
      this.updateImageField(value.value);
    }
  }

  private insertImageField(type: ImageFieldType, value: string): void {
    Word.run(async (context) => {
      try {
        const field: IFieldMeta = {
          namespace: this.getNamespace(),
          path: `${type}.image`, // leaf is used for the xpath element name
          label: type.toUpperCase(),
          type: type,
        };

        const fieldContext: FieldContext = new FieldContext(this.getNamespace(), IMAGE_FIELDS_ENTITY_NAME_LABEL_PAIR, field);
        fieldContext.imageFieldConfig = { type: type, value: value };

        await this.controllerService.insert(context, fieldContext);
        await context.sync();

        this.activeFieldContext = undefined;
        this.initialiseState(undefined);
        this.isValid = false;
      } catch (err: any) {
        this.loggingService.error('ImageFieldsComponent', 'insertImageField', {
          message: 'Failed to insert image field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert image field.', err);
      }
    });
  }

  private updateImageField(value: string): void {
    Word.run(async (context) => {
      try {
        let fieldContext = this.activeFieldContext!;
        fieldContext.imageFieldConfig!.value = value;

        fieldContext = await this.controllerService.update(context, fieldContext);

        await context.sync();

        this.activeFieldContext = fieldContext;
        this.initialiseState(fieldContext.imageFieldConfig);
        this.isValid = false;
      } catch (err: any) {
        this.loggingService.error('ImageFieldsComponent', 'updateImageField', {
          message: 'Failed to update image field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to update image field.', err);
      }
    });
  }

  private updateValidity(): void {
    if (this.imageSource === ImageFieldType.URL) {
      this.isValid = this.urlValue.value.length > 0;
    } else if (this.imageSource === ImageFieldType.ENTITY_FIELD) {
      this.isValid = this.entityFieldValue.value.length > 0;
    } else if (this.imageSource === ImageFieldType.SCRIPT_FIELD) {
      this.isValid = this.scriptFieldValue.value.length > 0;
    } else if (this.imageSource === ImageFieldType.FILE_TYPE) {
      this.isValid = this.selectedFileType !== undefined;
    } else {
      this.isValid = false;
    }
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cimage-field - active content control changed handler', 'color:#ffa0aa');
    if (this.isDocusignField(activeContentControl)) {
      this.valid.setValid(false);
      return;
    }
    const activeFieldContext = activeContentControl ? this.contentControlToFieldMapService.get(activeContentControl.id) : undefined;
    const activeImageField = this.isImageField(activeFieldContext) ? activeFieldContext : undefined;
    const isConditionalField = this.isConditionalField(activeFieldContext);
    this.activeFieldContext = this.isImageField(activeFieldContext) ? this.createFieldContext(activeFieldContext!) : undefined;
    this.mode = activeImageField ? Mode.UPDATE : Mode.INSERT;
    this.initialiseState(activeImageField?.imageFieldConfig);
    this.valid.setValid(<boolean>(!activeFieldContext || activeImageField || isConditionalField));
  }

  private createFieldContext(serializedFieldContext: SerializedFieldContext): FieldContext {
    const imageFieldConfig = { ...serializedFieldContext.imageFieldConfig! };
    const field: IFieldMeta = {
      namespace: this.getNamespace(),
      path: serializedFieldContext.path,
      label: imageFieldConfig.type.toUpperCase(),
      type: imageFieldConfig.type,
    };
    const fieldContext = new FieldContext(serializedFieldContext.namespace, IMAGE_FIELDS_ENTITY_NAME_LABEL_PAIR, field);
    fieldContext.imageFieldConfig = imageFieldConfig;
    return fieldContext;
  }

  private isImageField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === IMAGE_FIELDS_ENTITY_NAME;
  }

  private isConditionalField(activeFieldContext: SerializedFieldContext | undefined): boolean {
    return activeFieldContext?.entity === CONDITIONAL_FIELDS_ENTITY_NAME;
  }

  private isDocusignField(activeContentControl?: ContentControlId): boolean {
    return activeContentControl ? activeContentControl.tag.startsWith('docusign-') : false;
  }

  private getNamespace(): string {
    return this.identityStorage.get().namespace;
  }

  private initialiseState(srcConfig: ImageFieldOptions | undefined): void {
    this.toggleState = ToggleState.URL;
    this.imageSource = ImageFieldType.URL;
    this.urlValue.value = '';
    this.entityFieldValue.value = '';
    this.scriptFieldValue.value = '';
    this.selectedFileType = undefined;

    if (srcConfig) {
      const imageFieldConfig = { ...srcConfig };
      this.imageSource = imageFieldConfig.type;
      if (imageFieldConfig.type === ImageFieldType.URL) {
        this.urlValue = imageFieldConfig;
        this.toggleState = ToggleState.URL;
      } else if (imageFieldConfig.type === ImageFieldType.ENTITY_FIELD) {
        this.entityFieldValue = imageFieldConfig;
        this.fieldSource = FieldSource.ENTITY;
        this.toggleState = ToggleState.FIELD;
      } else if (imageFieldConfig.type === ImageFieldType.SCRIPT_FIELD) {
        this.scriptFieldValue = imageFieldConfig;
        this.fieldSource = FieldSource.SCRIPT;
        this.toggleState = ToggleState.FIELD;
      } else if (imageFieldConfig.type === ImageFieldType.FILE_TYPE) {
        this.selectedFileType = this.fileTypes.find((ft) => ft.id === imageFieldConfig.value);
        this.toggleState = ToggleState.FILE_TYPE;
      }
    }
  }
}
